import { of as observableOf, Subject, Observable, lastValueFrom, Subscriber } from 'rxjs';
import { Component, OnInit, input, output, OnDestroy, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UntypedFormGroup, UntypedFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { StateService } from '../../../../Common/States/States.Service';
import { StateSummary } from '../../../../Common/States/state.model';
import { Constants } from '../../../../constants.new';
import { FilingBatchService } from '../../filingBatch.service';
import { ComplianceInfoRepository } from '../../../Repositories/complianceInfo.repository';
import { mergeMap, startWith, takeUntil } from 'rxjs/operators';
import { HelpService } from '../../../../UI-Lib/Help-Tooltip';
import { HelpModule } from '../../../../UI-Lib/Help-Tooltip/help.module';
import {
    CHANGE_DETECTION_COMPANY_ASSET_LIST_HELP,
    CHANGE_DETECTION_PRIOR_RETURN_HELP,
    FILING_BATCH_INFO_HELP,
} from './filingBatchInfo.component.help';
import moment from 'moment-timezone';
import { YearPickerMixin } from '../../../../UI-Lib/Mixins';
import { MessageModalService } from '../../../../UI-Lib/Message-Box/messageModal.service';
import { SelectComponent } from '../../../../UI-Lib/Select/select.component';
import { WeissmanDatetimeComponent } from '../../../../UI-Lib/Weiss-Datepicker/weissman-datetime.component';
import { WeissmanDateFormatPipe } from '../../../../UI-Lib/Pipes';

/**
 * Shared filing batch form contents. Used both by the details-page panel wrapper
 * (`FilingBatchInfoPanelComponent`) and by `FilingBatchCreateModalComponent`.
 * Owns the form, its validators, and state/deadline loading for display purposes.
 * Purely handles data input and display - it does NOT perform the create/update
 * persistence call, and callers never reach into it to pull the current model. Instead,
 * this component pushes the up-to-date model to the caller via the `filingBatchChange`
 * output whenever the form changes, so the caller always has the latest values on hand
 * to persist itself. Does NOT own any panel/modal chrome (headers, expand/collapse, delete).
 */
@Component({
    selector: 'filing-batch-info',
    templateUrl: './filingBatchInfo.component.html',
    styleUrls: ['./filingBatchInfo.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SelectComponent,
        WeissmanDatetimeComponent,
        HelpModule,
        WeissmanDateFormatPipe,
    ],
})
export class FilingBatchInfoComponent extends YearPickerMixin() implements OnInit, OnDestroy {
    constructor(
        private readonly _stateService: StateService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly _constants: Constants,
        private readonly _filingBatchService: FilingBatchService,
        private readonly _complianceInfoRepository: ComplianceInfoRepository,
        private readonly _messageModalService: MessageModalService,
        private readonly _helpService: HelpService,
    ) {
        super();

        // react to the parent re-binding a new filing batch (e.g. selecting a different batch)
        effect(() => {
            this.filingBatch();
            if (this._isInitialized) {
                this._setModel();
            }
        });
    }

    /** The filing batch passed in by the parent, bound via `[filingBatch]`. */
    filingBatch = input<Compliance.FilingBatchModel>();

    /** Working copy derived from `filingBatch`, mutated locally (e.g. for add mode defaults). */
    model: Compliance.FilingBatchModel;

    /** Whether the shared form controls should render as editable inputs (true) or static text (false). */
    editMode = input<boolean>(false);

    formValidityChanged = output<boolean>();
    filingBatchChange = output<Compliance.FilingBatchModel>();

    addMode: boolean = false;
    states: StateSummary[] = [];
    statesTypeAheadDataSource: Observable<StateSummary[]>;
    statesTypeAheadAsyncSelected: any;

    // currently only supporting 'PP Return' filing type
    filingTypes: Compliance.NameValuePair<Core.FilingTypes>[] = [
        this._constants.FilingTypes.find((x) => x.value === Core.FilingTypes.PPReturn),
    ];

    changeDetectionOptions: Compliance.NameValuePair<boolean>[] = [
        { name: 'Prior Return', value: false },
        { name: 'Company Asset List', value: true },
    ];

    form: UntypedFormGroup = this._formBuilder.group({
        filingType: [null, [Validators.required]],
        taxYear: [null, [Validators.required]],
        description: [null, [Validators.required, Validators.maxLength(4000)]],
        dueDate: [null, Validators.required],
        changeDetection: [false],
        stateGroup: this._formBuilder.group({
            state: [null, [Validators.required]],
            stateName: [null],
        }),
    });

    private _isInitialized: boolean = false;
    private _destroy$: Subject<void> = new Subject();
    private _stateDeadlineMap: Map<number, Compliance.StateDeadlinesModel> = new Map<
        number,
        Compliance.StateDeadlinesModel
    >();

    customStateSearch = (values: any[], searchValue: string) => {
        const pattern = searchValue.split('').reduce((a, b) => `${a}[^${b}]*${b}`);
        const regex = new RegExp(pattern);
        return values
            .filter((o) => {
                return regex.test(o.fullName.toLowerCase()) || regex.test(o.abbr.toLowerCase());
            })
            .sort((a, b) => {
                // Sort by state abbreviation exact match first
                const aMatch = a.abbr.toLowerCase() === searchValue.toLowerCase();
                const bMatch = b.abbr.toLowerCase() === searchValue.toLowerCase();
                if (!aMatch && bMatch) {
                    return 1;
                } else if (aMatch && !bMatch) {
                    return -1;
                }
                return a.fullName.localeCompare(b.fullName);
            });
    };

    get f() {
        return this.form;
    }

    get fState() {
        return this.form.get('stateGroup') as UntypedFormGroup;
    }

    get changeDetection(): string {
        return this.form.get('changeDetection').value ? 'Company Assets' : 'Prior Return';
    }

    get changeDetectionHelpText(): string {
        return this.form.get('changeDetection').value
            ? CHANGE_DETECTION_COMPANY_ASSET_LIST_HELP
            : CHANGE_DETECTION_PRIOR_RETURN_HELP;
    }

    get tasksHelpContentId(): string {
        return this.editMode() ? 'app.edit-task-series' : 'app.view-task-series';
    }

    async ngOnInit(): Promise<void> {
        this._helpService.setContent(FILING_BATCH_INFO_HELP);

        this.form.statusChanges
            .pipe(startWith(this.form.status), takeUntil(this._destroy$))
            .subscribe(() => this.formValidityChanged.emit(this.form.valid));

        this.form.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => this.filingBatchChange.emit(this._buildModelFromForm()));

        this.states = await this._stateService.getSummary();

        this._setModel();

        this._filingBatchService.filingBatchChange$.pipe(takeUntil(this._destroy$)).subscribe((x) => {
            if (this.model && x && this.model.filingBatchId === x.filingBatchId) {
                this.model.filingCount = x.filingCount;
                this.model.rowVersion = x.rowVersion;
                this.model.processStatus = x.processStatus;
                this.model.status = x.status;
                this.model.statusDate = x.statusDate;
                this.model.getPriorReturnFromAssetList = x.getPriorReturnFromAssetList;

                this._updateFormFromModel();
            }
        });

        this.statesTypeAheadDataSource = (
            new Observable((observer: Subscriber<string>) => {
                observer.next(this.statesTypeAheadAsyncSelected);
            }) as Observable<string>
        ).pipe(mergeMap((token: string) => this.getStatesAsObservable(token)));

        const deadlines = await lastValueFrom(this._complianceInfoRepository.getDeadlines(this.model.companyId));
        this.states.forEach((state: StateSummary) => {
            const stateDeadlines = deadlines.find(
                (deadline: Compliance.StateDeadlinesModel) => state.stateID === deadline.stateId,
            );
            if (stateDeadlines) {
                this._stateDeadlineMap.set(state.stateID, stateDeadlines);
            }
        });

        this.form
            .get('changeDetection')
            .valueChanges.pipe(takeUntil(this._destroy$))
            .subscribe((x) => {
                // creating a new batch should never prompt for confirmation - there is nothing to replace yet
                if (this.addMode) {
                    return;
                }

                const message = x
                    ? 'All prior returns will be cleared, and all forms will be removed and replaced with default forms. Are you sure you wish to continue?'
                    : 'All forms will be removed and replaced with forms from the prior return (if available). Are you sure you wish to continue?';
                this._messageModalService.confirm(message, 'Confirm Change Detection Change').catch(() => {
                    this.form.get('changeDetection').patchValue(!x, { emitEvent: false });
                });
            });

        this._isInitialized = true;
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    getStatesAsObservable(token: string): Observable<StateSummary[]> {
        const query = new RegExp(token, 'ig');

        return observableOf(
            this.states.filter((state: StateSummary) => {
                return query.test(state.abbr) || query.test(state.fullName);
            }),
        );
    }

    onStateSelected(state: StateSummary): void {
        this.fState.patchValue({
            state: state,
            stateName: (state && state.fullName) || null,
        });
        this.updateDeadlineForStateAndTaxYear();
    }

    onDueDateChanged(value: Date | Event): void {
        this.f.controls.dueDate.markAsTouched();
        if (!(value instanceof Date)) {
            return;
        }

        this.f.patchValue({
            dueDate: value,
        });
    }

    updateDeadlineForStateAndTaxYear(): void {
        const taxYear = this.f.get('taxYear').value;
        const state: StateSummary = this.fState.get('state').value;
        if (taxYear && state) {
            const stateId = state.stateID;
            const deadline = this._findEarliestDeadlineForStateAndTaxYear(stateId, taxYear);

            if (deadline) {
                const day = deadline.deadlineMonthDay;
                if (day) {
                    const dueDate = this._getDateFromDeadlineAndTaxYear(deadline, taxYear);
                    this.f.controls.dueDate.setValue(dueDate);
                } else if (deadline.deadlineDate) {
                    this.f.controls.dueDate.setValue(deadline.deadlineDate);
                }
            }
        }
    }

    async viewTasks(): Promise<void> {
        await this._filingBatchService.viewTasks(this.model, this.editMode());
    }

    private _findEarliestDeadlineForStateAndTaxYear(stateId: number, taxYear: number): Core.AppealDeadlineModel {
        let earliestFoundDeadline = null;
        const stateDeadlines = this._stateDeadlineMap.get(stateId);
        if (stateDeadlines != null) {
            earliestFoundDeadline = stateDeadlines.stateDeadline;
            // find any earlier ones
            if (stateDeadlines.exceptionDeadlines !== null) {
                const exceptionsApplyingToThisYear = stateDeadlines.exceptionDeadlines.filter(
                    (ed: Core.AppealDeadlineModel) => ed.taxYear === null || ed.taxYear === taxYear,
                );
                exceptionsApplyingToThisYear.forEach((ad: Core.AppealDeadlineModel) => {
                    if (this._isSecondDeadlineEarlierThanFirst(earliestFoundDeadline, ad, taxYear)) {
                        earliestFoundDeadline = ad;
                    }
                });
            }
        }
        return earliestFoundDeadline;
    }

    private _getDateFromDeadlineAndTaxYear(deadline: Core.AppealDeadlineModel, taxYear: number) {
        const day = deadline ? deadline.deadlineMonthDay : null;
        if (day && taxYear) {
            return moment.utc(`${taxYear + deadline.deadlineYearID}/${day}`, 'YYYY-MM-DD').toDate();
        } else {
            return null;
        }
    }

    private async _setModel(): Promise<void> {
        const filingBatch = this.filingBatch();
        if (!filingBatch) {
            return;
        }

        this.addMode = filingBatch.filingBatchId === 0;

        if (this.addMode) {
            this.model = {
                filingBatchId: 0,
                displayId: null,
                filingTypeId: Core.FilingTypes.PPReturn,
                companyId: filingBatch.companyId,
                stateId: null,
                taxYear: new Date().getUTCFullYear(),
                dueDate: new Date(),
                description: null,
                rowVersion: null,
                // "Prior Return" is the default Change Detection source for new filing batches; Company Asset List is an explicit opt-in.
                getPriorReturnFromAssetList: false,
                reportNBVInsteadOfFactoredCost: false,
                returnCostReportingDisplayId: Compliance.ReturnCostReportingDisplayEnum.Both,
            };
        } else {
            // when not in add mode, model is being passed in as input parameter so use that, otherwise fetch from API
            this.model = filingBatch;
        }

        this._updateFormFromModel();

        this._setReadOnlyFormFields();
    }

    private _setReadOnlyFormFields(): void {
        // this one is always disabled as we're only allowing one filing type (PP Return) for now
        this.f.get('filingType').disable();

        if (!this.addMode) {
            this.f.get('taxYear').disable();
            this.fState.disable();
        }
    }

    private _updateFormFromModel(): void {
        const state = this.states.find((x) => x.stateID === this.model.stateId) || null;

        this.statesTypeAheadAsyncSelected = state && state.fullName;

        const filingType = this.filingTypes.find((x) => x.value === this.model.filingTypeId) || null;

        this.form.setValue(
            {
                filingType: filingType,
                taxYear: this.model.taxYear,
                description: this.model.description,
                dueDate: this.model.dueDate,
                changeDetection: this.model.getPriorReturnFromAssetList,
                stateGroup: {
                    state: state,
                    stateName: state && state.fullName,
                },
            },
            { emitEvent: false },
        );

        this.form.markAsPristine();
        this.form.markAsUntouched();
    }

    private _buildModelFromForm(): Compliance.FilingBatchModel {
        const filingType = this.f.get('filingType').value;
        const state: StateSummary = this.fState.get('state').value;

        return {
            ...this.model,
            filingTypeId: filingType ? filingType.value : this.model.filingTypeId, // bound to a <name, value> object
            taxYear: this.f.get('taxYear').value,
            description: this.f.get('description').value,
            dueDate: this.f.get('dueDate').value,
            getPriorReturnFromAssetList: this.f.get('changeDetection').value,
            stateId: state ? state.stateID : this.model.stateId,
            stateName: state ? state.fullName : this.model.stateName,
        };
    }

    private _isSecondDeadlineEarlierThanFirst(
        firstDeadline: Core.AppealDeadlineModel,
        secondDeadline: Core.AppealDeadlineModel,
        taxYear: number,
    ): boolean {
        const firstDate =
            firstDeadline.deadlineMonthDay !== null
                ? this._getDateFromDeadlineAndTaxYear(firstDeadline, taxYear)
                : firstDeadline.deadlineDate;
        const secondDate =
            secondDeadline.deadlineMonthDay !== null
                ? this._getDateFromDeadlineAndTaxYear(secondDeadline, taxYear)
                : secondDeadline.deadlineDate;
        return secondDate < firstDate;
    }
}
